import * as React from "react";
import { useMemo } from "react";

import { BindingPath } from "../../../../../Engine/Core/CoreTypes";
import { ERROR_LOG_NAVIGATE_CLICK } from "../../../../metricsAction";
import { useMetrics } from "../../../../Internals/Metrics/MetricsProviderContext";
import {
    ErrorLogItem,
    ItemErrorMessage,
    ItemErrorMessageType,
} from "../../../../../Engine/Core/ErrorManager/Utils/Models";
import { safeHTML } from "../../../../../Engine/Core/ErrorManager/Utils/Utils";

import * as styles from "./ErrorItemDescription.styles";
import { getPreparedText, hasHiddenTextInDescription, replaceExternalLink } from "./Utils/Utils";

interface ErrorItemDescriptionProps {
    inTooltip: boolean;
    error: ErrorLogItem;
    onActivateItem: (paths: BindingPath[], highlanderUrl?: string) => Promise<void>;
    doNotRenderNavigationLinks?: boolean;
}

export function ErrorItemDescription(props: ErrorItemDescriptionProps): JSX.Element {
    const track = useMetrics();
    const hideLinks = useMemo(() => hasHiddenTextInDescription(props.error.description), []);

    const handleExternalLinkClick = (pathForArgs: undefined | string, place: string): void => {
        track(ERROR_LOG_NAVIGATE_CLICK, { path: pathForArgs, fromTooltip: props.inTooltip, place: place });
    };

    const handleNavClick = async (paths: BindingPath[], place: string, highlanderUrl?: string) => {
        track(ERROR_LOG_NAVIGATE_CLICK, { path: paths?.[0] ?? "", fromTooltip: props.inTooltip, place: place });
        await props.onActivateItem(paths, highlanderUrl);
    };

    return (
        <>
            {props.error.description.map((path: ItemErrorMessage, index: number) => {
                switch (path.type) {
                    case ItemErrorMessageType.external:
                        return (
                            <a
                                rel="noreferrer"
                                css={styles.link}
                                href={replaceExternalLink(path.url)}
                                key={index}
                                target="_blank"
                                onClick={() => handleExternalLinkClick(replaceExternalLink(path.url), "externalLink")}>
                                <span
                                    data-tid="DescriptionItemText"
                                    dangerouslySetInnerHTML={{ __html: safeHTML(path.text) }}
                                />
                            </a>
                        );
                    case ItemErrorMessageType.navLink:
                        return props.doNotRenderNavigationLinks || hideLinks ? (
                            <span
                                data-tid="DescriptionItemText"
                                dangerouslySetInnerHTML={{ __html: safeHTML(path.text) }}
                                key={index}
                            />
                        ) : (
                            <span
                                data-tid="DescriptionNavLink"
                                css={styles.link}
                                key={index}
                                onClick={() => handleNavClick(path.bindingPaths, "description", path.highlanderUrl)}>
                                <span
                                    data-tid="DescriptionItemText"
                                    dangerouslySetInnerHTML={{ __html: safeHTML(path.text) }}
                                />
                            </span>
                        );
                    default: {
                        const preparedText = getPreparedText(path.text);
                        return (
                            <span data-tid="DescriptionItemText" key={index}>
                                {preparedText}
                            </span>
                        );
                    }
                }
            })}
        </>
    );
}
